/**
 * The lead: the name and contact details a person gives a firm before they sign in to fill its
 * snapshot (2026-10-07).
 *
 * Two shapes live here so the public route and the Lambda that records the lead cannot drift:
 *
 *   - {@link IntakeLeadFieldsSchema}: what the browser sends, minus transport fields.
 *   - {@link IntakeLeadPayloadSchema}: the ONE payload the lead Lambda accepts on a synchronous
 *     invoke from the public function. It is a `strictObject`, so that function cannot be made
 *     to do anything else by a payload naming another operation.
 *
 * `sharingConsent` is the boolean the browser sets when the person agreed to the sentence
 * shown above the button. The recorder composes the sentence itself from the firm's stored
 * name (`intakeSharingConsentSentence`) and stamps it with its own clock; a payload cannot
 * supply either. Without `sharingConsent: true` nothing is recorded.
 *
 * What is RECORDED is narrower than what is accepted: the recorder keeps the address and the
 * consent pair, and drops `firstName`, `lastName`, `phone` and `invitationCode`
 * (`services/intake-writer/src/__tests__/lead.test.ts` holds that). They stay in the schema
 * because the form sends them and the schema is strict.
 */
import { z } from 'zod';
import { ADVISOR_FIRM_SLUG_PATTERN } from './advisorFirmDirectory.js';
import { INTAKE_INVITATION_CODE_PATTERN } from './intakeDraft.js';
import { INTAKE_HONEYPOT_FIELD } from './intakeSchema.js';

/** Version of the lead payload. A strict literal: a mismatched deploy fails closed. */
export const INTAKE_LEAD_PAYLOAD_VERSION = 1;

/** Longest first or last name accepted on a lead. */
export const INTAKE_LEAD_NAME_MAX = 100;

/** Longest phone number accepted on a lead. */
export const INTAKE_LEAD_PHONE_MAX = 40;

/** No control characters in a name. */
const NAME_SAFE = /^[^\u0000-\u001F\u007F-\u009F￾￿]+$/u;

const name = z.string().trim().min(1).max(INTAKE_LEAD_NAME_MAX).regex(NAME_SAFE);

/** First name, last name and email are required; phone is optional. */
export const IntakeLeadFieldsSchema = z.strictObject({
  firstName: name,
  lastName: name,
  email: z.email().max(254),
  phone: z.string().trim().min(1).max(INTAKE_LEAD_PHONE_MAX).regex(/^[0-9+().\-\sx#]+$/i).optional(),
  invitationCode: z.string().regex(INTAKE_INVITATION_CODE_PATTERN).optional(),
  sharingConsent: z.boolean().optional(),
});

export type IntakeLeadFields = z.infer<typeof IntakeLeadFieldsSchema>;

/**
 * Every key the public lead route (`POST /intake/{firmSlug}/lead`) accepts in its JSON body:
 * the lead fields above, plus the two bot-defence fields the browser sends beside them. The
 * route's schema is strict, so a body carrying any other key is dropped.
 * `services/steward-app-api/src/__tests__/intakeRequestKeys.test.ts` checks that the route's
 * schema declares exactly these keys.
 */
export const INTAKE_LEAD_REQUEST_KEYS = [
  'firstName', 'lastName', 'email', 'phone', 'invitationCode', 'sharingConsent',
  'turnstileToken', INTAKE_HONEYPOT_FIELD,
] as const;
export type IntakeLeadRequestKey = (typeof INTAKE_LEAD_REQUEST_KEYS)[number];

export const IntakeLeadPayloadSchema = z.strictObject({
  v: z.literal(INTAKE_LEAD_PAYLOAD_VERSION),
  op: z.literal('lead'),
  firmSlug: z.string().regex(ADVISOR_FIRM_SLUG_PATTERN),
  /** SHA-256 hex of the caller's source address, computed by the public function. */
  ipHash: z.string().regex(/^[0-9a-f]{64}$/),
  requestId: z.string().max(200),
  lead: IntakeLeadFieldsSchema,
});

export type IntakeLeadPayload = z.infer<typeof IntakeLeadPayloadSchema>;

/** Parse an invoke payload. Returns `undefined` for anything that is not exactly a lead. */
export function parseIntakeLeadPayload(value: unknown): IntakeLeadPayload | undefined {
  const parsed = IntakeLeadPayloadSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}
